import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle2, AlertTriangle, Shield, ExternalLink, ArrowRight } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What does IMPORTRANGE do?',
    options: [
      'Imports data from a CSV file',
      'Pulls data from another Google Sheet',
      'Exports data to another sheet',
      'Imports data from a database',
    ],
    correctIndex: 1,
    explanation:
      'IMPORTRANGE pulls data from another Google Sheet into your current sheet using the source spreadsheet\'s URL and a range reference.',
  },
  {
    question: 'What happens when you share a sheet that contains IMPORTRANGE?',
    options: [
      'The IMPORTRANGE stops working',
      'Nothing — the data is copied statically',
      'The person you share with gains access to the source sheet\'s data',
      'Google requires re-authentication',
    ],
    correctIndex: 2,
    explanation:
      'When Sheet B has an IMPORTRANGE pointing at Sheet A, and you share Sheet B with someone, they effectively gain read access to the data in Sheet A that the IMPORTRANGE pulls.',
  },
  {
    question: 'Why is it dangerous to IMPORTRANGE master data into a client sheet?',
    options: [
      'It makes the sheet slow',
      'The client could modify the formula to access other offers\' data',
      'IMPORTRANGE has a row limit',
      'The data becomes read-only',
    ],
    correctIndex: 1,
    explanation:
      'If a client has edit access to a sheet with IMPORTRANGE pointing at your master data, they could change the range reference to pull different data — potentially seeing other offers\' confidential information.',
  },
  {
    question: 'True or false: IMPORTRANGE data updates automatically',
    options: [
      'True — changes in the source appear in the destination',
      'False — you must manually refresh',
      'True, but only once per day',
      'False — it only imports once',
    ],
    correctIndex: 0,
    explanation:
      'IMPORTRANGE creates a live connection. When data changes in the source spreadsheet, the destination spreadsheet\'s IMPORTRANGE results update automatically (though there may be a short delay).',
  },
  {
    question: 'What is the safe alternative to direct IMPORTRANGE into client sheets?',
    options: [
      'Email the data instead',
      'Use a bridge sheet in between',
      'Password-protect the IMPORTRANGE',
      'Use VLOOKUP instead',
    ],
    correctIndex: 1,
    explanation:
      'The Bridge Sheet Pattern puts an intermediary sheet between your master data and the client. The bridge only contains the client\'s data, so even if they gain access, they can only see their own information.',
  },
  {
    question: 'What two arguments does IMPORTRANGE require?',
    options: [
      'A file name and a password',
      'A spreadsheet URL and a range string (e.g. "Sheet1!A1:D100")',
      'A sheet ID and a column letter',
      'A database connection string and a table name',
    ],
    correctIndex: 1,
    explanation:
      'IMPORTRANGE takes exactly two arguments: the full URL of the source spreadsheet and a string specifying the sheet name and cell range to import.',
  },
  {
    question: 'What happens the first time you use IMPORTRANGE with a new source spreadsheet?',
    options: [
      'It works immediately with no extra steps',
      'Google asks you to click "Allow access" to grant permission',
      'You need to enter a password',
      'The source sheet owner must send you an invitation',
    ],
    correctIndex: 1,
    explanation:
      'The first time you use IMPORTRANGE with a new source, Google shows an "Allow access" prompt. Once you click it, that permission persists for all future IMPORTRANGE calls between those two spreadsheets.',
  },
  {
    question: 'If Sheet B has IMPORTRANGE pointing at Sheet A, and you share Sheet B with a client, what can the client potentially access?',
    options: [
      'Only the cells currently displayed in Sheet B',
      'Any range in Sheet A, by editing the IMPORTRANGE formula',
      'Only Sheet A\'s first tab',
      'Nothing — IMPORTRANGE is read-only and locked',
    ],
    correctIndex: 1,
    explanation:
      'Once IMPORTRANGE access is granted between two spreadsheets, anyone with edit access to the destination sheet can change the range argument to pull any data from the source sheet.',
  },
  {
    question: 'Which of these is a valid reason to use IMPORTRANGE?',
    options: [
      'To keep a single source of truth and build multiple reports from it',
      'To make your spreadsheet load faster',
      'To encrypt data between sheets',
      'To bypass Google Sheets\' row limit',
    ],
    correctIndex: 0,
    explanation:
      'IMPORTRANGE lets you maintain one master dataset and pull from it into multiple report sheets. This avoids duplicating data and ensures all reports stay in sync with the source.',
  },
  {
    question: 'What does "live connection" mean in the context of IMPORTRANGE?',
    options: [
      'You must keep the source sheet open for it to work',
      'Changes in the source sheet automatically flow to the destination sheet',
      'Both sheets must be edited at the same time',
      'The connection expires after 24 hours',
    ],
    correctIndex: 1,
    explanation:
      'A live connection means the destination sheet automatically reflects changes made in the source sheet — you don\'t need to re-import or refresh manually.',
  },
  {
    question: 'A client has edit access to their report sheet. The sheet uses IMPORTRANGE from your master data. What should you do?',
    options: [
      'Nothing — edit access is fine as long as you trust the client',
      'Remove the IMPORTRANGE and paste static data instead',
      'Add a bridge sheet between the master and the client sheet',
      'Change the client\'s access to comment-only',
    ],
    correctIndex: 2,
    explanation:
      'A bridge sheet isolates the client from your master data. Even if the client edits their IMPORTRANGE formula, they can only reach the bridge — which contains only their offer\'s data.',
  },
  {
    question: 'Once IMPORTRANGE access has been granted between two spreadsheets, how do you revoke it?',
    options: [
      'Click "Revoke access" in the IMPORTRANGE dialog',
      'Delete the IMPORTRANGE formula',
      'There is no built-in revoke — you must remove sharing permissions on the source sheet',
      'Change the source sheet\'s password',
    ],
    correctIndex: 2,
    explanation:
      'Google Sheets does not provide a simple "revoke IMPORTRANGE access" button. To cut the connection, you need to remove the destination sheet\'s access to the source via sharing settings or move the data to a new source.',
  },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Security Danger Animation ─── */
function DangerScenario() {
  const [step, setStep] = useState(0)

  const steps = [
    {
      label: 'Normal setup',
      description: 'You create a client sheet with IMPORTRANGE pointing at your master data.',
      clientFormula: '=IMPORTRANGE("master_url", "Combined!A:E")',
      status: 'normal',
    },
    {
      label: 'You share the sheet',
      description: 'You share the client sheet with the client so they can see their report.',
      clientFormula: '=IMPORTRANGE("master_url", "Combined!A:E")',
      status: 'shared',
    },
    {
      label: 'Client edits the formula',
      description: 'The client modifies the IMPORTRANGE range to see different data...',
      clientFormula: '=IMPORTRANGE("master_url", "Combined!A:Z")',
      status: 'danger',
    },
    {
      label: 'Data breach!',
      description: 'The client can now see ALL offers\' data, including competitors\' metrics.',
      clientFormula: '=IMPORTRANGE("master_url", "Combined!A:Z")',
      status: 'breach',
    },
  ]

  const current = steps[step]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {steps.map((s, i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              i === step
                ? i >= 2
                  ? 'bg-red-600 text-white'
                  : 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          className={`rounded-lg border-2 p-5 ${
            current.status === 'breach'
              ? 'border-red-400 bg-red-50'
              : current.status === 'danger'
              ? 'border-amber-400 bg-amber-50'
              : 'border-gray-200 bg-gray-50'
          }`}
        >
          <div className="flex items-start gap-3">
            {current.status === 'breach' && <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />}
            {current.status === 'danger' && <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />}
            <div className="flex-1 space-y-3">
              <p className={`text-sm font-semibold ${current.status === 'breach' ? 'text-red-800' : current.status === 'danger' ? 'text-amber-800' : 'text-gray-800'}`}>
                {current.description}
              </p>
              <div className="rounded-lg bg-gray-900 px-3 py-2">
                <code className={`text-xs font-mono ${current.status === 'breach' || current.status === 'danger' ? 'text-red-400' : 'text-blue-400'}`}>
                  {current.clientFormula}
                </code>
              </div>
              {current.status === 'breach' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-lg border border-red-300 bg-red-100 p-3"
                >
                  <p className="text-sm text-red-800 font-bold">
                    The client can now see every offer's clicks, conversions, and performance data.
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module11() {
  return (
    <ModuleLayout moduleId={13}>
      <div className="space-y-8">
        {/* Section 1: What is IMPORTRANGE? */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">What is IMPORTRANGE?</h2>
              <p className="text-lg text-gray-600">
                IMPORTRANGE pulls data from another Google Sheet into your current sheet.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-base font-mono">
                <span className="text-gray-500">=</span>
                <span className="text-red-400">IMPORTRANGE</span>
                <span className="text-gray-500">(</span>
                <span className="text-green-300">"spreadsheet_url"</span>
                <span className="text-gray-500">, </span>
                <span className="text-amber-300">"Sheet1!A1:D100"</span>
                <span className="text-gray-500">)</span>
              </code>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="bg-green-900/40 text-green-300 rounded px-2 py-0.5">spreadsheet_url = the other spreadsheet's URL</span>
                <span className="bg-amber-900/40 text-amber-300 rounded px-2 py-0.5">range = which sheet and cells to import</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-6 py-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-16 h-16 rounded-xl bg-blue-100 border-2 border-blue-300 flex items-center justify-center">
                  <ExternalLink className="w-7 h-7 text-blue-600" />
                </div>
                <p className="text-xs font-medium text-blue-700">Source Sheet</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <ArrowRight className="w-8 h-8 text-gray-300" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-16 h-16 rounded-xl bg-green-100 border-2 border-green-300 flex items-center justify-center">
                  <ExternalLink className="w-7 h-7 text-green-600" />
                </div>
                <p className="text-xs font-medium text-green-700">Your Sheet</p>
              </motion.div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                <strong>First-time setup:</strong> The first time you use IMPORTRANGE with a new source spreadsheet, Google will ask you to click "Allow access". This grants your sheet permission to read from the source.
              </p>
            </div>
          </div>
        </Section>

        {/* Section 2: Why It's Useful */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Why It's Useful</h2>
              <p className="text-lg text-gray-600">
                Keep your master data in one sheet and pull it into other sheets as needed.
              </p>
            </div>

            <div className="space-y-3">
              {[
                { text: 'Changes in the source automatically appear in the destination', icon: CheckCircle2, color: 'emerald' },
                { text: 'You can keep one "single source of truth" and build many reports from it', icon: CheckCircle2, color: 'emerald' },
                { text: 'Your master combined dataset lives in one sheet, reports pull from it', icon: CheckCircle2, color: 'emerald' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4"
                >
                  <item.icon className={`w-5 h-5 text-${item.color}-500 shrink-0 mt-0.5`} />
                  <p className={`text-sm text-${item.color}-800`}>{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </Section>

        {/* Section 3: The Danger */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <h2 className="mb-1 text-2xl font-bold text-red-800">The Danger</h2>
                <p className="text-lg text-red-600">
                  This is the most important security concept in this entire course.
                </p>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="rounded-xl border-2 border-red-400 bg-red-50 p-6 space-y-4"
            >
              <p className="text-red-800 font-semibold text-lg">
                When you IMPORTRANGE from Sheet A into Sheet B, Sheet B gets ACCESS to Sheet A.
              </p>
              <p className="text-red-700">
                If you share Sheet B with a client, and Sheet B has an IMPORTRANGE pointing at your master data...
              </p>
              <p className="text-red-800 font-bold text-lg">
                The client could modify the IMPORTRANGE formula to pull DIFFERENT ranges — seeing other offers' data!
              </p>
            </motion.div>

            <DangerScenario />

            <div className="rounded-xl border-2 border-red-300 bg-red-50 p-5 text-center">
              <p className="text-red-800 font-bold text-lg">
                This is why you NEVER IMPORTRANGE directly from your master data into a client-facing sheet.
              </p>
            </div>
          </div>
        </Section>

        {/* Section 4: The Safe Way (Preview) */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Safe Way (Preview)</h2>
              <p className="text-lg text-gray-600">
                In the next module, you will learn the Bridge Sheet Pattern — the safe way to use IMPORTRANGE for client reports.
              </p>
            </div>

            <div className="flex items-center justify-center gap-4 py-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="rounded-xl border-2 border-blue-300 bg-blue-50 px-5 py-3 text-center"
              >
                <p className="text-xs font-bold text-blue-600">Master</p>
                <p className="text-sm text-blue-800 font-semibold">All Data</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-3 text-center"
              >
                <Shield className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                <p className="text-xs font-bold text-amber-600">Bridge</p>
                <p className="text-sm text-amber-800 font-semibold">One Offer Only</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-5 py-3 text-center"
              >
                <p className="text-xs font-bold text-emerald-600">Client</p>
                <p className="text-sm text-emerald-800 font-semibold">Safe Report</p>
              </motion.div>
            </div>
          </div>
        </Section>

        {/* Section 5: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={13} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
